import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';
import { warehouseApi as api, apiError } from '../shared/warehouseApi';

export function ProductSelector({ userId, providerId, selectedId, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [up, setUp] = useState(false);
  const [maxHeight, setMaxHeight] = useState(384);
  const root = useRef(null);
  const trigger = useRef(null);

  useEffect(() => {
    if (!open) return;
    const close = event => { if (!root.current?.contains(event.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  useEffect(() => {
    if (!open || !trigger.current) return;
    const rect = trigger.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const placeUp = spaceBelow < 320 && rect.top > spaceBelow;
    setUp(placeUp);
    setMaxHeight(Math.max(80, Math.min(placeUp ? rect.top - 8 : spaceBelow - 8, 384)));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setLoading(true); setError('');
      try {
        const { data } = await api.get('/api/admin/packages/filter-products', {
          params: { user_id: userId || undefined, provider_id: providerId || undefined, q: search, page, per_page: 25 },
          signal: controller.signal,
        });
        if (!controller.signal.aborted) {
          setItems(previous => page === 1 ? data.items : [...previous, ...data.items]);
          setHasNext(Boolean(data.pagination?.has_next));
        }
      } catch (err) {
        if (!controller.signal.aborted) setError(apiError(err));
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, page === 1 ? 250 : 0);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [open, search, page, userId, providerId]);

  const changeSearch = value => { setSearch(value); setPage(1); setItems([]); };
  const choose = item => {
    setSelected(item); onChange(item?.id || ''); setOpen(false);
    changeSearch('');
  };

  return <div ref={root} className="relative min-w-0">
    <button ref={trigger} type="button" aria-label="Seleccionar producto" aria-expanded={open}
      onClick={() => setOpen(value => !value)}
      className="flex w-full min-w-0 items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-left text-sm hover:border-gray-300">
      <span className={`min-w-0 flex-1 truncate ${selectedId ? 'font-semibold text-gray-700' : 'text-gray-400'}`}>
        {selectedId ? (selected?.title || `Producto #${selectedId}`) : 'Todos los productos'}
      </span>
      {selectedId ? <span role="button" tabIndex={0} aria-label="Limpiar producto" onClick={event => { event.stopPropagation(); choose(null); }}
        onKeyDown={event => { if (event.key === 'Enter') { event.stopPropagation(); choose(null); } }}
        className="shrink-0 text-gray-400 hover:text-gray-700"><X size={15}/></span>
        : <ChevronDown size={15} className="shrink-0 text-gray-400"/>}
    </button>
    {open && <div className={`absolute left-0 z-50 flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl ${up ? 'bottom-full mb-1' : 'top-full mt-1'}`}
      style={{ width: 'min(36rem, calc(100vw - 2rem))', maxHeight }}>
      <div className="relative border-b border-gray-100 p-2">
        <Search size={15} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"/>
        <input autoFocus type="search" aria-label="Buscar producto" value={search} onChange={event => changeSearch(event.target.value)}
          placeholder="Buscar por nombre, SKU o ID" className="w-full rounded-lg border border-gray-200 bg-gray-50 py-2 pl-8 pr-3 text-sm focus:border-brand-primary focus:outline-none"/>
      </div>
      <div className="min-h-0 overflow-y-auto">
        {items.map(item => <button type="button" key={item.id} onClick={() => choose(item)}
          className="flex w-full items-start justify-between gap-3 border-b border-gray-50 px-3 py-2.5 text-left text-sm hover:bg-gray-50">
          <span className="min-w-0">
            <span className="block break-words font-semibold text-gray-800">{item.title} <span className="font-normal text-gray-400">#{item.id}</span></span>
            <span className="block break-words text-xs text-gray-500">SKU {item.sku} · {item.seller_name}{item.is_deleted ? ' · Archivado' : ''}</span>
          </span>
          <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold ${item.available_quantity > 0 ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}
            title={item.stock_basis === 'recorded' ? 'Saldo registrado; pendiente de conciliación física' : 'Disponible: físico menos reservado y dañado'}>
            {item.available_quantity} {item.stock_basis === 'recorded' ? 'registradas*' : 'libres'}
          </span>
        </button>)}
        {loading && <p className="p-3 text-sm text-gray-500">Cargando productos...</p>}
        {error && <p role="alert" className="p-3 text-sm text-red-600">{error}</p>}
        {!loading && !error && !items.length && <p className="p-3 text-sm text-gray-500">Sin productos</p>}
        {hasNext && !loading && <button type="button" onClick={() => setPage(value => value + 1)} className="w-full p-3 text-sm font-semibold text-brand-primary">Cargar más</button>}
      </div>
      {items.some(item => item.stock_basis === 'recorded') && <p className="border-t border-gray-100 p-2 text-xs text-amber-700">* Saldo registrado, aún no conciliado físicamente.</p>}
    </div>}
  </div>;
}
